import {
	ArrowLeftOutlined,
	ArrowRightOutlined,
	CloseOutlined,
	DeleteOutlined,
	DownloadOutlined,
} from "@ant-design/icons"
import { ErrorCodeEnum, ErrorResponse } from "api/api"
import { Button } from "components/Button/Button"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Image } from "components/Image/Image"
import { ZoomableImage } from "components/Image/ZoomableImage"
import { Modal } from "components/Modal/Modal"
import { useNotification } from "components/Notification/Notification"
import { Scrollable } from "components/Scrollable/Scrollable"
import { ShortcutLabel } from "components/ShortcutLabel/ShortcutLabel"
import { Tooltip } from "components/Tooltip/Tooltip"
import { useDisclosure } from "hooks/useDisclosure"
import { useElement } from "hooks/useElement"
import { WeightIcon } from "icons/WeightIcon"
import { uniqBy } from "lodash-es"
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { createPortal } from "react-dom"
import { useHotkeys } from "react-hotkeys-hook"
import { Link, useLocation, useNavigate, useParams } from "react-router"
import {
	PAGE_SIZE,
	useDeleteDataPoint,
	usePatientData,
	usePatientDetails,
	usePatientScreening,
} from "services/patients"
import { cn } from "styles/cn"
import { asDate, dateFormatter } from "utils/date"
import { downloadFileFromUrl } from "utils/file"
import { ReviewForm } from "./ReviewForm"

const NEIGHBOUR_PREFETCH_DISTANCE = 2
const SKELETON_THUMBNAILS = [0, 1, 2, 3, 4]

export function Screenings() {
	const { scopeId, userId, dataId } = useParams()
	const { notify } = useNotification()
	const deleteDisclose = useDisclosure()
	const thumbnails = useDisclosure({ defaultIsOpen: true })
	const image = useDisclosure()
	const navigate = useNavigate()
	const location = useLocation()
	const containerElement = useElement("layout-content")
	const { data: { fullName } = {} } = usePatientDetails(userId)
	const [anchorDataId, setAnchorDataId] = useState(() => dataId)
	// The strip is revealed in one piece, so it stays behind skeletons until it
	// holds enough rows to stop widening on its own. See the effect that sets it.
	const [isStripFilled, setIsStripFilled] = useState(false)
	const {
		data: { pages = [], total } = {},
		hasNextPage,
		fetchNextPage,
		hasPreviousPage,
		fetchPreviousPage,
		isError,
		isFetched,
		isFetching,
		isFetchingNextPage,
		isFetchNextPageError,
		isFetchPreviousPageError,
	} = usePatientData({
		userId,
		anchorDataId,
	})
	const screenings = useMemo(
		() =>
			uniqBy(
				pages.toSorted(
					(a, b) => asDate(a.capturedAt).unix() - asDate(b.capturedAt).unix(),
				),
				"id",
			),

		[pages],
	)
	const {
		data: heroScreening,
		error: heroError,
		isFetched: isHeroFetched,
	} = usePatientScreening({
		userId,
		dataId,
	})
	const deleteDataPoint = useDeleteDataPoint(userId)
	const isLoading = isFetching || isFetchingNextPage
	const currentIndex = screenings.findIndex(({ id }) => id === dataId)
	const isListPending = currentIndex < 0
	const isListLost = isListPending && isError
	const isStripReady = !isListPending && isStripFilled
	const screening =
		screenings[currentIndex] ??
		(heroScreening?.id === dataId ? heroScreening : undefined)
	const isHeroMissing =
		(heroError as ErrorResponse | null)?.code ===
		ErrorCodeEnum.DataIdNotFoundError
	const dataPointDate = dateFormatter(screening?.capturedAt, { hasYear: false })
	const targetRef = useRef<HTMLLIElement | null>(null)
	const handledMissingDataIdRef = useRef<string | undefined>(undefined)
	const reportedListErrorRef = useRef(false)
	const pendingNavigationRef = useRef<{
		direction: "next" | "previous"
		fromDataId?: string
	} | null>(null)
	useLayoutEffect(() => {
		if (!thumbnails.isOpen || !isStripReady) {
			return
		}
		targetRef.current?.scrollIntoView({ inline: "center", block: "nearest" })
	}, [isStripReady, screening?.id, thumbnails.isOpen])
	const navigateBack = useCallback(() => {
		const backgroundLocation = (location.state as any)?.backgroundLocation
		if (backgroundLocation) {
			navigate(backgroundLocation.pathname + backgroundLocation.search)
			return
		}
		navigate(`/${scopeId}/patients/${userId}${location.search}`)
	}, [location.search, location.state, navigate, scopeId, userId])
	const handleNavigate = useCallback(
		(id?: string) => {
			image.toggle()

			// If no ID is provided, it means we are closing the modal.
			if (!id) {
				navigateBack()
				return
			}

			navigate(
				`/${scopeId}/patients/${userId}/m/screenings/${id}${location.search}`,
			)
		},
		[image, location.search, navigate, navigateBack, scopeId, userId],
	)
	const handleZoomUpdate = useCallback(
		(zoom: number) => {
			if (zoom > 1) {
				thumbnails.close()
			} else {
				thumbnails.open()
			}
		},
		[thumbnails],
	)
	const handleDownload = useCallback(async () => {
		if (!screening || !screening.imageS3Key) {
			return
		}
		await downloadFileFromUrl(
			screening.imageS3Key,
			`${fullName} - ${screening.capturedAt}`,
		)
	}, [fullName, screening])
	const handleDelete = useCallback(() => {
		if (!screening || !screening.id) {
			return
		}
		deleteDataPoint.mutate(screening.id, {
			onSuccess() {
				const previousScreening = screenings[currentIndex - 1]
				const nextScreening = screenings[currentIndex + 1]
				const neighbourDataId = (previousScreening ?? nextScreening)?.id
				setAnchorDataId(neighbourDataId)
				setIsStripFilled(false)
				deleteDisclose.close()
				notify.success({
					message: "Delete data point",
					description: `Data point captured at ${dataPointDate} deleted successfully.`,
				})
				handleNavigate(neighbourDataId)
			},
		})
	}, [
		currentIndex,
		dataPointDate,
		deleteDisclose,
		deleteDataPoint,
		handleNavigate,
		notify,
		screening,
		screenings,
	])
	const handlePrevious = useCallback(() => {
		const previousScreening = screenings[currentIndex - 1]
		if (previousScreening) {
			handleNavigate(previousScreening.id)
			return
		}
		if (currentIndex === 0 && hasNextPage) {
			pendingNavigationRef.current = {
				direction: "previous",
				fromDataId: dataId,
			}
			void fetchNextPage()
		}
	}, [
		currentIndex,
		dataId,
		fetchNextPage,
		handleNavigate,
		hasNextPage,
		screenings,
	])
	const handleNext = useCallback(() => {
		const nextScreening = screenings[currentIndex + 1]
		if (nextScreening) {
			handleNavigate(nextScreening.id)
			return
		}
		if (currentIndex === screenings.length - 1 && hasPreviousPage) {
			pendingNavigationRef.current = { direction: "next", fromDataId: dataId }
			void fetchPreviousPage()
		}
	}, [
		currentIndex,
		dataId,
		fetchPreviousPage,
		handleNavigate,
		hasPreviousPage,
		screenings,
	])

	useHotkeys("esc", () => handleNavigate())
	useHotkeys("shift+D", handleDownload)
	useHotkeys("shift+crtl+D", handleDownload)
	useHotkeys("ArrowLeft", handlePrevious)
	useHotkeys("ArrowRight", handleNext)

	useEffect(() => {
		if (currentIndex < 0 || isLoading) {
			return
		}
		const canLoadOlder = hasNextPage && !isFetchNextPageError
		const canLoadNewer = hasPreviousPage && !isFetchPreviousPageError
		if (currentIndex <= NEIGHBOUR_PREFETCH_DISTANCE && canLoadOlder) {
			void fetchNextPage()
			return
		}
		if (
			currentIndex >= screenings.length - 1 - NEIGHBOUR_PREFETCH_DISTANCE &&
			canLoadNewer
		) {
			void fetchPreviousPage()
			return
		}
		if (screenings.length < PAGE_SIZE) {
			if (canLoadNewer) {
				void fetchPreviousPage()
				return
			}
			if (canLoadOlder) {
				void fetchNextPage()
				return
			}
		}
		setIsStripFilled(true)
	}, [
		currentIndex,
		fetchNextPage,
		fetchPreviousPage,
		hasNextPage,
		hasPreviousPage,
		isFetchNextPageError,
		isFetchPreviousPageError,
		isLoading,
		screenings.length,
	])

	useEffect(() => {
		const pendingNavigation = pendingNavigationRef.current
		if (!pendingNavigation) {
			return
		}
		if (pendingNavigation.fromDataId !== dataId) {
			pendingNavigationRef.current = null
			return
		}
		if (currentIndex < 0) {
			return
		}
		if ((isFetchNextPageError || isFetchPreviousPageError) && !isLoading) {
			pendingNavigationRef.current = null
			return
		}
		const neighbour =
			screenings[
				pendingNavigation.direction === "next"
					? currentIndex + 1
					: currentIndex - 1
			]
		if (!neighbour) {
			return
		}
		pendingNavigationRef.current = null
		handleNavigate(neighbour.id)
	}, [
		currentIndex,
		dataId,
		handleNavigate,
		isFetchNextPageError,
		isFetchPreviousPageError,
		isLoading,
		screenings,
	])

	useEffect(() => {
		if (!isError || !screening || reportedListErrorRef.current) {
			return
		}
		reportedListErrorRef.current = true
		notify.error({
			message: "Failed to load data point",
			description:
				"We could not load this patient's data points. Please try again.",
		})
	}, [isError, notify, screening])

	useEffect(() => {
		if (!dataId || !isHeroMissing) {
			return
		}
		if (handledMissingDataIdRef.current === dataId) {
			return
		}
		handledMissingDataIdRef.current = dataId
		notify.warning({
			message: "Data point not found",
			description:
				"This data point is no longer available. It may have been deleted.",
		})
		navigateBack()
	}, [dataId, isHeroMissing, navigateBack, notify])

	useEffect(() => {
		if (!dataId || isFetching || !isFetched || !isHeroFetched) {
			return
		}
		if (handledMissingDataIdRef.current === dataId) {
			return
		}
		if (isListPending && dataId !== anchorDataId) {
			setAnchorDataId(dataId)
			setIsStripFilled(false)
			return
		}
		if (screening) {
			return
		}
		handledMissingDataIdRef.current = dataId

		if (isError) {
			notify.error({
				message: "Failed to load data point",
				description:
					"We could not load this patient's data points. Please try again.",
			})
		} else {
			notify.warning({
				message: "Data point not found",
				description:
					"This data point is no longer available. It may have been deleted.",
			})
		}

		navigateBack()
	}, [
		anchorDataId,
		dataId,
		isError,
		isFetched,
		isFetching,
		isHeroFetched,
		isListPending,
		navigateBack,
		notify,
		screening,
	])

	if (!containerElement) {
		return null
	}

	return createPortal(
		<div
			role="dialog"
			aria-label={`Data point at ${dataPointDate}`}
			className="absolute grid size-full grid-rows-[1fr_auto] bg-black @5xl/content:grid-cols-[1fr_572px]"
		>
			{screening && (
				<>
					<div className="@container mx-auto flex size-full flex-col gap-4 overflow-hidden p-2">
						<ZoomableImage
							alt={`Data point at ${dataPointDate}`}
							src={screening.imageWebpS3Key ?? screening.imageS3Key}
							className="relative h-full"
							imgClassName="object-contain"
							errorMessage={
								<>
									No image available <br />
									at {dataPointDate}
								</>
							}
							onZoomUpdate={handleZoomUpdate}
							onLoad={image.open}
							toolbarLeft={() => (
								<Tooltip.Dark
									title={<ShortcutLabel label="Close" shortcut="ESC" />}
								>
									<Button
										variant="filled"
										color="default"
										shape="circle"
										size="large"
										aria-label="Close"
										className="mr-auto"
										icon={<CloseOutlined aria-hidden />}
										onClick={() => handleNavigate()}
									/>
								</Tooltip.Dark>
							)}
							toolbarRight={(isError) => (
								<div className="ml-auto flex gap-5">
									{!isError && (
										<Tooltip.Dark
											title={<ShortcutLabel label="Download" shortcut="D" />}
										>
											<Button
												variant="filled"
												color="default"
												size="large"
												aria-label={`Download data point at ${dataPointDate}`}
												icon={<DownloadOutlined aria-hidden />}
												onClick={handleDownload}
											/>
										</Tooltip.Dark>
									)}
									<Tooltip.Dark title="Delete">
										<Button
											variant="filled"
											color="red"
											size="large"
											aria-label={`Delete data point at ${dataPointDate}`}
											icon={<DeleteOutlined aria-hidden />}
											onClick={deleteDisclose.open}
										/>
									</Tooltip.Dark>
								</div>
							)}
						>
							{!isListPending && (currentIndex > 0 || hasNextPage) && (
								<Tooltip.Dark
									title={<ShortcutLabel label="Previous" shortcut="←" />}
								>
									<Button
										variant="filled"
										color="default"
										size="large"
										aria-label="Load previous data points"
										className="absolute top-1/2 left-6"
										icon={<ArrowLeftOutlined aria-hidden />}
										onClick={handlePrevious}
									/>
								</Tooltip.Dark>
							)}

							{!isListPending &&
								(currentIndex < screenings.length - 1 || hasPreviousPage) && (
									<Tooltip.Dark
										title={<ShortcutLabel label="Next" shortcut="→" />}
									>
										<Button
											variant="filled"
											color="default"
											size="large"
											aria-label="Load next data points"
											className="absolute top-1/2 right-6"
											icon={<ArrowRightOutlined aria-hidden />}
											onClick={handleNext}
										/>
									</Tooltip.Dark>
								)}
						</ZoomableImage>

						{!isListLost && (
							<div className="grid place-content-center">
								<Scrollable.ReverseHorizontal
									aria-label={
										isLoading || !isStripReady
											? "Loading data points"
											: `Displaying ${screenings.length} of ${total} data points`
									}
									className={cn("hidden py-3", thumbnails.isOpen && "flex")}
									size={isStripReady ? screenings.length : 0}
									scroll={{
										hasNextPage:
											isStripReady && hasNextPage && !isFetchNextPageError,
										loading: isLoading,
										onLoadMore: fetchNextPage,
									}}
									scrollEnd={{
										hasNextPage:
											isStripReady &&
											hasPreviousPage &&
											!isFetchPreviousPageError,
										loading: isLoading,
										onLoadMore: fetchPreviousPage,
									}}
								>
									{!isStripReady &&
										SKELETON_THUMBNAILS.map((position) => (
											<li
												key={position}
												aria-hidden
												className="size-14 animate-pulse rounded-sm bg-navy-80"
											/>
										))}

									{isStripReady &&
										screenings.map(
											({ id, imageThumbS3Key, isReviewed, capturedAt }) => {
												const title = dateFormatter(capturedAt, {
													hasYear: false,
												})

												return (
													<li
														key={`${id}-${imageThumbS3Key}`}
														ref={id === dataId ? targetRef : undefined}
														className="space-y-3"
													>
														<Tooltip.Dark title={title}>
															<div
																className={cn(
																	"flex flex-col items-center justify-center gap-2",
																	"rounded-sm outline-offset-4 outline-green-20",
																	id === dataId && "outline-2",
																)}
															>
																<Link
																	to={`/${scopeId}/patients/${userId}/m/screenings/${id}`}
																	aria-label={`Go to data point at ${title}`}
																	className="size-14"
																	onClick={image.toggle}
																>
																	{!imageThumbS3Key && (
																		<div className="flex items-center justify-center">
																			<WeightIcon.Null className="size-12 text-navy-60" />
																		</div>
																	)}

																	{!!imageThumbS3Key && (
																		<Image
																			rootClassName="size-full"
																			className="rounded-sm object-cover"
																			src={imageThumbS3Key}
																		/>
																	)}
																</Link>
															</div>
														</Tooltip.Dark>
														<div
															className={cn(
																"invisible mx-auto size-2 rounded-full bg-blue-60",
																!isReviewed && "visible",
															)}
														/>
													</li>
												)
											},
										)}
								</Scrollable.ReverseHorizontal>
							</div>
						)}
					</div>

					<ReviewForm
						key={`patient-data-review-${screening.id}`}
						dataPoint={screening}
						previusDataPoint={
							currentIndex > 0 ? screenings[currentIndex - 1] : undefined
						}
						disabled={!image.isOpen}
					/>
				</>
			)}

			{deleteDisclose.isOpen && (
				<Modal
					okButtonProps={{ danger: true }}
					open={deleteDisclose.isOpen}
					confirmLoading={deleteDataPoint.isPending}
					title={`Do you want to delete the data point captured at ${dataPointDate}?`}
					okText="Delete data point"
					cancelText="Keep data point"
					onOk={handleDelete}
					onCancel={deleteDisclose.close}
					classNames={{ body: "flex flex-col gap-2" }}
				>
					This will permanently delete the data point from the patient's record.
					<ErrorStatus show={deleteDataPoint.isError}>
						An unexpected error occurred while deleting the data point at{" "}
						{dataPointDate}. Please, try again.
					</ErrorStatus>
				</Modal>
			)}
		</div>,
		containerElement,
	)
}
